import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getCountryConfig, countriesConfig } from '../lib/countryData';
import AddressAutocompleteWrapper from './AddressAutocomplete';

export default function AddressFormGroup({
    addressData,
    onChange,
    className = ""
}) {
    const defaultCountry = 'ht';
    const [selectedCountry, setSelectedCountry] = useState(addressData?.countryCode || defaultCountry);

    // Initial sync from props
    useEffect(() => {
        if (addressData?.countryCode && addressData.countryCode !== selectedCountry) {
            setSelectedCountry(addressData.countryCode);
        }
    }, [addressData?.countryCode, selectedCountry]);

    const handleCountryChange = (code) => {
        setSelectedCountry(code);

        // When country changes, clear out region/city/zip specific to old country
        onChange({
            ...addressData,
            countryCode: code,
            address: "",
            city: "",
            region: "",
            zipCode: "",
            lat: null,
            lng: null
        });
    };

    const handleAddressSelect = (loc) => {
        onChange({
            ...addressData,
            address: loc.address,
            city: loc.city || addressData?.city || "",
            region: loc.region || addressData?.region || "",
            zipCode: loc.zipCode || addressData?.zipCode || "",
            lat: loc.coordinates?.lat || null,
            lng: loc.coordinates?.lng || loc.coordinates?.lon || null,
        });
    };

    const handleChange = (field, value) => {
        onChange({
            ...addressData,
            [field]: value
        });
    };

    const config = getCountryConfig(selectedCountry);

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Country Selection */}
            <div className="space-y-2">
                <Label>Pays de résidence *</Label>
                <Select value={selectedCountry} onValueChange={handleCountryChange}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sélectionnez un pays" />
                    </SelectTrigger>
                    <SelectContent>
                        {countriesConfig.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                                {country.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Manual Edit Fields (Pre-filled if found by map) */}
            <div className={`grid gap-4 ${config.hasPostalCode ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2'}`}>
                {/* Admin Area (State/Region/Dept) */}
                <div className="space-y-2">
                    <Label>{config.administrativeAreaLabel} *</Label>
                    {config.departments ? (
                        <Select
                            value={addressData?.region || undefined}
                            onValueChange={(val) => {
                                onChange({
                                    ...addressData,
                                    region: val,
                                    city: ""
                                });
                            }}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={`Sélectionner un ${config.administrativeAreaLabel.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent>
                                {config.departments.map(dept => (
                                    <SelectItem key={dept.name} value={dept.name}>{dept.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            placeholder={`Saisir ${config.administrativeAreaLabel.toLowerCase()}`}
                            value={addressData?.region || ""}
                            onChange={(e) => handleChange('region', e.target.value)}
                        />
                    )}
                </div>

                {/* City */}
                <div className="space-y-2">
                    <Label>Ville *</Label>
                    {config.departments ? (
                        <Select
                            value={addressData?.city || undefined}
                            onValueChange={(val) => handleChange('city', val)}
                            disabled={!addressData?.region}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Sélectionner une ville" />
                            </SelectTrigger>
                            <SelectContent>
                                {config.departments.find(d => d.name === addressData?.region)?.cities.map(city => (
                                    <SelectItem key={city} value={city}>{city}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            placeholder="Saisir la ville"
                            value={addressData?.city || ""}
                            onChange={(e) => handleChange('city', e.target.value)}
                        />
                    )}
                </div>

                {/* Postal Code */}
                {config.hasPostalCode && (
                    <div className="space-y-2">
                        <Label>Code Postal *</Label>
                        <Input
                            placeholder="Ex: 75001"
                            value={addressData?.zipCode || ""}
                            onChange={(e) => handleChange('zipCode', e.target.value)}
                        />
                    </div>
                )}
            </div>

            {/* Address Autocomplete & Map */}
            <div className="space-y-2 border-t pt-4">
                <Label>Rechercher l'adresse exacte *</Label>
                <span className="text-xs text-muted-foreground block mb-2">
                    (Veuillez rechercher votre rue, numéro ou lieu-dit pour placer le repère sur la carte)
                </span>
                <AddressAutocompleteWrapper
                    countryCode={selectedCountry}
                    regionContext={addressData?.region}
                    cityContext={addressData?.city}
                    defaultValue={addressData?.address || ""}
                    onLocationSelect={(loc) => {
                        onChange({
                            ...addressData,
                            address: loc.address,
                            // Ne pas écraser la ville et région si déjà sélectionnées et qu'on est en Haïti (où on veut forcer la sélection manuelle)
                            city: (config.departments && addressData?.city) ? addressData.city : (loc.city || addressData?.city || ""),
                            region: (config.departments && addressData?.region) ? addressData.region : (loc.region || addressData?.region || ""),
                            zipCode: loc.zipCode || addressData?.zipCode || "",
                            lat: loc.coordinates?.lat || null,
                            lng: loc.coordinates?.lng || loc.coordinates?.lon || null,
                        });
                    }}
                />
            </div>

            {/* Precision (Apt, Suite, Details) */}
            <div className="space-y-2">
                <Label>Apt, Suite, Détails (Optionnel)</Label>
                <Input
                    placeholder="Appartement complet, bloc, etc."
                    value={addressData?.address_details || ""}
                    onChange={(e) => handleChange('address_details', e.target.value)}
                />
            </div>
        </div>
    );
}
